import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Sparkles, LayoutDashboard, CreditCard, BarChart3, LogOut, Menu, X,
  TrendingUp, Settings, ChevronDown, Palette, ChevronLeft, Package,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useWordPress } from '../../context/WordPressContext';
import { useState, useRef, useEffect } from 'react';

const standaloneNavItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/analytics', icon: TrendingUp, label: 'Analytics' },
  { to: '/widget-customizer', icon: Palette, label: 'Widget' },
  { to: '/subscription', icon: CreditCard, label: 'Subscription' },
  { to: '/usage', icon: BarChart3, label: 'Usage' },
];

const wordpressNavItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/analytics', icon: TrendingUp, label: 'Analytics' },
  { to: '/widget-customizer', icon: Palette, label: 'Widget' },
  { to: '/wp-reports', icon: BarChart3, label: 'Reports' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export default function Sidebar() {
  const { user, signOut } = useAuth();
  const { isInWordPress } = useWordPress();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const navItems = isInWordPress ? wordpressNavItems : standaloneNavItems;

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  async function handleSignOut() {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  const userInitial = user?.email?.charAt(0).toUpperCase() || 'U';
  const userName = user?.email?.split('@')[0] || 'User';

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center h-16 px-4 border-b border-surface-200 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center flex-shrink-0 shadow-sm">
          <Sparkles size={18} className="text-white" />
        </div>
        {!collapsed && (
          <span className="text-base font-bold text-surface-800 truncate">Virtual Try-On</span>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              title={collapsed ? item.label : undefined}
              className={`group relative flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150 ${
                collapsed ? 'justify-center px-3 py-2.5' : 'px-3 py-2.5'
              } ${
                isActive
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-surface-500 hover:bg-surface-100 hover:text-surface-700'
              }`}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-brand-600" />
              )}
              <item.icon size={18} className={isActive ? 'text-brand-600' : 'text-surface-400 group-hover:text-surface-600'} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Collapse toggle (desktop only) */}
      <div className="hidden lg:block px-3 pb-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center p-2 rounded-lg text-surface-400 hover:bg-surface-100 hover:text-surface-600 transition-colors"
        >
          <ChevronLeft size={16} className={`transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* User section */}
      {!isInWordPress && (
        <div className="border-t border-surface-200 px-3 py-3" ref={userMenuRef}>
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={`w-full flex items-center gap-3 rounded-xl p-2.5 hover:bg-surface-100 transition-colors ${collapsed ? 'justify-center' : ''}`}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {userInitial}
              </div>
              {!collapsed && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium text-surface-700 truncate">{userName}</div>
                    <div className="text-xs text-surface-400 truncate">{user?.email}</div>
                  </div>
                  <ChevronDown size={14} className={`text-surface-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>

            {userMenuOpen && (
              <div className={`absolute bottom-full mb-2 ${collapsed ? 'left-full ml-2' : 'left-0 right-0'} min-w-[200px] bg-white rounded-xl shadow-dropdown border border-surface-200 py-1.5 z-50 animate-scale-in`}>
                <Link
                  to="/account"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3.5 py-2 text-sm text-surface-600 hover:bg-surface-50 transition-colors"
                >
                  <Settings size={15} className="text-surface-400" />
                  Account Settings
                </Link>
                <Link
                  to="/subscription"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3.5 py-2 text-sm text-surface-600 hover:bg-surface-50 transition-colors"
                >
                  <CreditCard size={15} className="text-surface-400" />
                  Subscription
                </Link>
                <div className="border-t border-surface-200 my-1" />
                <button
                  onClick={() => { setUserMenuOpen(false); handleSignOut(); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors"
                >
                  <LogOut size={15} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WordPress footer */}
      {isInWordPress && (
        <div className="border-t border-surface-200 px-4 py-3">
          <a
            href="https://virtuas.ai"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs text-surface-400 hover:text-surface-500 transition-colors ${collapsed ? 'hidden' : ''}`}
          >
            Powered by Virtuas.ai
          </a>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile hamburger trigger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-40 p-2 rounded-xl bg-white shadow-card border border-surface-200 text-surface-600 hover:bg-surface-50"
      >
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-surface-900/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="relative w-64 h-full bg-white shadow-panel animate-slide-in-left">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 p-1.5 rounded-lg text-surface-400 hover:bg-surface-100"
            >
              <X size={18} />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 bg-white border-r border-surface-200 z-30 transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
